import { useState } from "react";
import { Mail, Lock, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerDescription,
} from "./ui/drawer";
import { Button } from "./ui/button";
import {
  requestMagicLink,
  registerAccount,
  loginWithPassword,
  requestPasswordReset,
} from "../lib/api";
import { useAuth } from "../lib/auth";
import { useAuthStatus } from "../lib/useAuthStatus";
import { useIsMobile } from "../lib/useIsMobile";

function formatApiErrorDetail(detail, fallback) {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : ""))
      .filter(Boolean)
      .join(" ") || fallback;
  if (detail && typeof detail.message === "string") return detail.message;
  if (detail && typeof detail.msg === "string") return detail.msg;
  return fallback;
}

const PASSWORD_MIN = 8;

export default function AuthModal({ open, onOpenChange, onAuthed }) {
  // view: login | register | forgot | magicSent | forgotSent
  const [view, setView] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const isMobile = useIsMobile();
  const { completeTokenSignIn } = useAuth();
  const { magicLinkEnabled } = useAuthStatus();

  const reset = () => {
    setView("login");
    setPassword("");
    setConfirm("");
    setError("");
    setSubmitting(false);
  };

  const finishSignedIn = (data, message) => {
    const u = completeTokenSignIn(data);
    toast.success(message, {
      description: `Signed in as ${u.email}. Your saved routines and checks are ready.`,
    });
    onOpenChange(false);
    reset();
    onAuthed?.();
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) return;
    setSubmitting(true);
    try {
      const data = await loginWithPassword(email, password);
      finishSignedIn(data, "Welcome back");
    } catch (err) {
      setError(
        formatApiErrorDetail(
          err?.response?.data?.detail,
          "We couldn't sign you in. Check your email and password.",
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    setError("");
    if (!email) return;
    if (password.length < PASSWORD_MIN) {
      setError(`Choose a password of at least ${PASSWORD_MIN} characters.`);
      return;
    }
    if (password !== confirm) {
      setError("Those passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      const data = await registerAccount(email, password);
      finishSignedIn(data, "Account created");
    } catch (err) {
      setError(
        formatApiErrorDetail(
          err?.response?.data?.detail,
          "We couldn't create that account. Please try again.",
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setError("");
    if (!email) return;
    setSubmitting(true);
    try {
      await requestPasswordReset(email);
      setView("forgotSent");
    } catch (err) {
      setError(
        formatApiErrorDetail(
          err?.response?.data?.detail,
          "We couldn't start a reset just now. Please try again.",
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const sendMagicLink = async () => {
    setError("");
    if (!email) {
      setError("Enter your email first.");
      return;
    }
    setSubmitting(true);
    try {
      await requestMagicLink(email);
      setView("magicSent");
    } catch (err) {
      setError(
        formatApiErrorDetail(
          err?.response?.data?.detail,
          "We couldn't send that sign-in link just now.",
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const title = {
    login: "Welcome back",
    register: (
      <>
        Create your{" "}
        <span className="font-editorial italic font-normal text-peach-500">
          Blendsafe
        </span>{" "}
        account
      </>
    ),
    forgot: "Reset your password",
    magicSent: "Check your email.",
    forgotSent: "Check your email.",
  }[view];

  const desc = {
    login: "Log in with your email and password.",
    register: "Save your routines, checks and discussion reports.",
    forgot: "We'll email you a link to set a new password.",
    magicSent: `We sent a one-tap sign-in link to ${email}. It expires in 15 minutes.`,
    forgotSent: `If an account exists for ${email}, a reset link is on its way. Check spam if it doesn't arrive within a minute.`,
  }[view];

  const inputCls =
    "w-full h-14 pl-11 pr-4 rounded-2xl bg-white border border-surface-line text-[15px] text-ink placeholder:text-ink-light focus:outline-none focus:ring-4 focus:ring-peach-100 focus:border-peach-200 transition-all";
  const labelCls =
    "text-[12px] font-medium text-ink-mute uppercase tracking-[0.18em] block mb-2";
  const primaryBtn =
    "w-full h-14 rounded-2xl bg-ink hover:bg-ink-soft text-cream-50 text-[15px] font-medium transition-all hover:scale-[1.01] active:scale-[0.99]";

  const body = (
    <div className="p-6 sm:p-9 pb-8 sm:pb-9">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-ink-mute font-semibold">
        <Sparkles className="w-3.5 h-3.5 text-peach-500" />
        {view === "forgot" || view === "forgotSent" ? "Password reset" : "Your account"}
      </div>

      {isMobile ? (
        <DrawerTitle className="font-bold text-2xl sm:text-3xl mt-3 tracking-[-0.02em] text-ink text-left">
          {title}
        </DrawerTitle>
      ) : (
        <DialogTitle className="font-bold text-2xl sm:text-3xl mt-4 tracking-[-0.02em] text-ink">
          {title}
        </DialogTitle>
      )}

      {isMobile ? (
        <DrawerDescription className="text-ink-soft/85 mt-2 text-[15px] leading-relaxed text-left">
          {desc}
        </DrawerDescription>
      ) : (
        <DialogDescription className="text-ink-soft/85 mt-3 text-[15px] leading-relaxed">
          {desc}
        </DialogDescription>
      )}

      {/* Tabs — login / create account */}
      {(view === "login" || view === "register") && (
        <div
          data-testid="auth-tabs"
          className="mt-6 grid grid-cols-2 p-1 rounded-2xl bg-cream-100 border border-surface-line"
        >
          <button
            type="button"
            data-testid="auth-tab-login"
            onClick={() => { setView("login"); setError(""); }}
            className={`h-10 rounded-xl text-[13px] font-semibold transition-all ${
              view === "login" ? "bg-white text-ink shadow-sm" : "text-ink-mute"
            }`}
          >
            Log in
          </button>
          <button
            type="button"
            data-testid="auth-tab-register"
            onClick={() => { setView("register"); setError(""); }}
            className={`h-10 rounded-xl text-[13px] font-semibold transition-all ${
              view === "register" ? "bg-white text-ink shadow-sm" : "text-ink-mute"
            }`}
          >
            Create account
          </button>
        </div>
      )}

      {error && (
        <div
          data-testid="auth-error"
          className="mt-5 rounded-xl bg-avoid-bg border border-avoid-border text-avoid-deep text-[13px] px-4 py-3"
        >
          {error}
        </div>
      )}

      {/* Login */}
      {view === "login" && (
        <form onSubmit={submitLogin} className="mt-5 space-y-4">
          <div>
            <label className={labelCls} htmlFor="auth-email">Email address</label>
            <div className="relative">
              <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-ink-light" />
              <input
                id="auth-email"
                data-testid="auth-email-input"
                type="email"
                required
                autoComplete="email"
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
              />
            </div>
          </div>
          <div>
            <label className={labelCls} htmlFor="auth-password">Password</label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-ink-light" />
              <input
                id="auth-password"
                data-testid="auth-password-input"
                type="password"
                required
                autoComplete="current-password"
                placeholder="Your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls}
              />
            </div>
          </div>
          <button
            type="button"
            data-testid="auth-forgot-link"
            onClick={() => { setView("forgot"); setError(""); }}
            className="text-[13px] text-peach-600 hover:text-peach-500 transition-colors"
          >
            Forgot password?
          </button>
          <Button type="submit" data-testid="auth-submit-btn" disabled={submitting} className={primaryBtn}>
            {submitting ? "Signing in…" : "Log in"}
            {!submitting && <ArrowRight className="w-4 h-4 ml-2" />}
          </Button>
        </form>
      )}

      {/* Register */}
      {view === "register" && (
        <form onSubmit={submitRegister} className="mt-5 space-y-4">
          <div>
            <label className={labelCls} htmlFor="auth-email-r">Email address</label>
            <div className="relative">
              <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-ink-light" />
              <input
                id="auth-email-r"
                data-testid="auth-email-input"
                type="email"
                required
                autoComplete="email"
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
              />
            </div>
          </div>
          <div>
            <label className={labelCls} htmlFor="auth-password-r">Password</label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-ink-light" />
              <input
                id="auth-password-r"
                data-testid="auth-password-input"
                type="password"
                required
                autoComplete="new-password"
                placeholder={`At least ${PASSWORD_MIN} characters`}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls}
              />
            </div>
          </div>
          <div>
            <label className={labelCls} htmlFor="auth-confirm-r">Confirm password</label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-ink-light" />
              <input
                id="auth-confirm-r"
                data-testid="auth-confirm-input"
                type="password"
                required
                autoComplete="new-password"
                placeholder="Re-enter your password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className={inputCls}
              />
            </div>
          </div>
          <Button type="submit" data-testid="auth-submit-btn" disabled={submitting} className={primaryBtn}>
            {submitting ? "Creating account…" : "Create account"}
            {!submitting && <ArrowRight className="w-4 h-4 ml-2" />}
          </Button>
          <p className="text-[12px] text-ink-light text-center pt-1">
            By continuing you agree to our terms. We never share your email.
          </p>
        </form>
      )}

      {/* Forgot password */}
      {view === "forgot" && (
        <form onSubmit={submitForgot} className="mt-5 space-y-4">
          {!magicLinkEnabled && (
            <div
              data-testid="auth-reset-dormant-note"
              className="rounded-xl bg-cream-100 border border-surface-line text-ink-soft text-[13px] px-4 py-3"
            >
              Email delivery isn't switched on yet, so a reset link can't be sent
              right now. If you're locked out, please contact support.
            </div>
          )}
          <div>
            <label className={labelCls} htmlFor="auth-email-f">Email address</label>
            <div className="relative">
              <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-ink-light" />
              <input
                id="auth-email-f"
                data-testid="auth-forgot-email-input"
                type="email"
                required
                autoComplete="email"
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
              />
            </div>
          </div>
          <Button
            type="submit"
            data-testid="auth-forgot-submit-btn"
            disabled={submitting || !magicLinkEnabled}
            className={primaryBtn}
          >
            {submitting ? "Sending…" : "Send reset link"}
            {!submitting && <ArrowRight className="w-4 h-4 ml-2" />}
          </Button>
          <button
            type="button"
            data-testid="auth-back-to-login"
            onClick={() => { setView("login"); setError(""); }}
            className="text-[13px] text-ink-mute hover:text-ink transition-colors w-full text-center min-h-[44px]"
          >
            Back to log in
          </button>
        </form>
      )}

      {/* Magic-link sent / forgot sent confirmation */}
      {(view === "magicSent" || view === "forgotSent") && (
        <div className="mt-6 sm:mt-7 space-y-4">
          <div
            data-testid={view === "magicSent" ? "auth-magic-sent-card" : "auth-forgot-sent-card"}
            className="rounded-2xl bg-white border border-surface-line p-5 flex items-start gap-3"
          >
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-safe-bg text-safe-deep border border-safe-border shrink-0">
              <Mail className="w-4 h-4" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-bold text-ink">
                {view === "magicSent" ? "Sign-in link sent" : "Reset link sent"}
              </div>
              <p className="text-[13px] text-ink-soft mt-1 leading-snug">
                Sent to {email}. Check spam if it doesn't arrive within a minute.
              </p>
            </div>
          </div>
          <button
            data-testid="auth-back-to-login"
            onClick={() => { setView("login"); setError(""); }}
            type="button"
            className="text-[13px] text-ink-mute hover:text-ink transition-colors w-full text-center min-h-[44px]"
          >
            Back to log in
          </button>
        </div>
      )}

      {/* Optional magic-link shortcut (only when email is configured) */}
      {magicLinkEnabled && (view === "login" || view === "register") && (
        <div className="mt-5">
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.2em] text-ink-light">
            <span className="h-px flex-1 bg-surface-line" />
            or
            <span className="h-px flex-1 bg-surface-line" />
          </div>
          <button
            type="button"
            data-testid="auth-magic-btn"
            onClick={sendMagicLink}
            disabled={submitting}
            className="mt-4 w-full h-12 rounded-2xl bg-white border border-surface-line text-ink text-[14px] font-medium hover:bg-cream-100 transition-colors"
          >
            Email me a one-tap link instead
          </button>
        </div>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}
      >
        <DrawerContent
          data-testid="auth-modal"
          className="bg-cream-50 border-surface-line max-h-[94vh]"
        >
          <div className="overflow-y-auto">{body}</div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}
    >
      <DialogContent
        data-testid="auth-modal"
        className="sm:max-w-md p-0 overflow-hidden rounded-3xl border border-surface-line bg-cream-50 shadow-floating max-h-[94vh] overflow-y-auto"
      >
        {body}
      </DialogContent>
    </Dialog>
  );
}
